'use client';

import React, { useEffect, useRef } from 'react';

export default function About() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const stats = [
    { value: '3+', label: 'Years Experience' },
    { value: '10+', label: 'Projects Delivered' },
    { value: '3', label: 'Industries Served' },
    { value: 'BE', label: 'Based in Belgium' },
  ];

  return (
    <section id="about" className="bg-white py-24 md:py-32 overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5 md:px-16 lg:px-24">
        {/* Section label */}
        <div className="animate-on-scroll">
          <span className="section-label">01 — About Me</span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: Heading */}
          <div>
            <h2
              className="section-title animate-on-scroll"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
            >
              Turning
              <br />
              <span className="text-primary">Strategy</span>
              <br />
              Into Impact
            </h2>

            {/* Stats row */}
            <div className="mt-12 grid grid-cols-2 gap-6 animate-on-scroll">
              {stats?.map((stat) => (
                <div key={stat?.label} className="border-t-2 border-primary pt-4">
                  <div
                    className="font-condensed font-bold text-primary"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
                  >
                    {stat?.value}
                  </div>
                  <div className="font-condensed text-xs tracking-widest uppercase text-dark opacity-60 mt-1">
                    {stat?.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Bio text */}
          <div className="space-y-6">
            <p
              className="font-serif text-dark animate-on-scroll"
              style={{ fontSize: 'clamp(1.1rem, 1.5vw, 1.3rem)', lineHeight: '1.7', fontStyle: 'italic' }}
            >
              I&rsquo;m a Digital Marketing&nbsp; and Digital Communications Specialist with a background in
              International Communication and&nbsp; Media, currently based in Belgium.
            </p>

            <p className="font-sans text-dark opacity-80 leading-relaxed animate-on-scroll" style={{ fontSize: '1rem' }}>
              My work sits at the intersection of content strategy, performance marketing, and
              brand communication. I&rsquo;ve had the privilege of working across diverse environments
              — from fast-paced digital agencies to international NGOs — which has sharpened my
              ability to adapt messaging for different audiences and cultures.
            </p>

            <p className="font-sans text-dark opacity-80 leading-relaxed animate-on-scroll" style={{ fontSize: '1rem' }}>
              I believe great marketing is built on three pillars: <strong>data</strong> that
              informs decisions, <strong>content</strong> that connects emotionally, and
              <strong> strategy</strong> that ties it all together. Whether I&rsquo;m crafting an
              SEO-optimized article, managing a social campaign, or building a brand narrative,
              I bring the same level of precision and creativity.
            </p>

            <p className="font-sans text-dark opacity-80 leading-relaxed animate-on-scroll" style={{ fontSize: '1rem' }}>
              Multicultural by experience and growth-oriented by nature, I&rsquo;m always looking
              for the next challenge that pushes me to think differently and deliver better results.
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 pt-2 animate-on-scroll">
              {[
                { label: 'Content Strategy', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
                { label: 'Performance Marketing', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
                { label: 'Brand Communication', bg: '#ffffff', text: '#7c3aed', border: '#7c3aed' },
                { label: 'Multicultural', bg: '#7c3aed', text: '#ffffff', border: '#7c3aed' },
                { label: 'Data-Driven', bg: '#0ECAD4', text: '#ffffff', border: '#0ECAD4' },
              ]?.map((tag) => (
                <span
                  key={tag?.label}
                  className="font-condensed text-xs font-bold tracking-wider uppercase px-3 py-1.5 cursor-default transition-all duration-300"
                  style={{ backgroundColor: tag?.bg, color: tag?.text, border: `1.5px solid ${tag?.border}` }}
                >
                  {tag?.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}